/**
 * Tela /meus-dados — TH-55255 / área do usuário.
 *
 * Permite ao usuário autenticado:
 *   - Ver e editar o próprio nome (PUT /api/auth/me).
 *   - Ver email (read-only — campo bloqueado no backend).
 *   - Ver role + tenant_id (read-only — contexto operacional).
 *   - Trocar senha (POST /api/auth/change-password).
 *
 * Não há toast lib global; feedback inline via banner de sucesso/erro
 * abaixo de cada bloco (padrão das demais páginas do admin).
 *
 * Validações:
 *   - Nome: obrigatório, ≥ 2 chars.
 *   - Nova senha: mínimo 12 chars (LGPD Cl. 8.1.c — bcrypt cost 12).
 *   - Confirmar nova senha: bate com nova senha.
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { AppLayout } from "@/components/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Skeleton } from "@/components/ui/Skeleton";
import { useAuth } from "@/hooks/useAuth";
import { useUpdateMe } from "@/hooks/useUpdateMe";
import { useChangePassword } from "@/hooks/useChangePassword";

// ── Schemas ────────────────────────────────────────────────────────────────

const profileSchema = z.object({
  name: z.string().min(2, "Nome precisa ter pelo menos 2 caracteres").max(200),
});
type ProfileForm = z.infer<typeof profileSchema>;

const passwordSchema = z
  .object({
    current_password: z.string().min(1, "Informe a senha atual"),
    new_password: z
      .string()
      .min(12, "A nova senha precisa ter no mínimo 12 caracteres"),
    confirm_password: z.string().min(1, "Confirme a nova senha"),
  })
  .refine((d) => d.new_password === d.confirm_password, {
    message: "As senhas não coincidem",
    path: ["confirm_password"],
  });
type PasswordForm = z.infer<typeof passwordSchema>;

// ── Helpers ────────────────────────────────────────────────────────────────

const ROLE_LABEL: Record<string, string> = {
  super_admin: "Super Admin",
  tenant_admin: "Admin do cliente",
  store_manager: "Gerente de loja",
  operator: "Operador",
  catalog_operator: "Operador de catálogo",
  salesperson: "Vendedor",
};

// ── ProfileCard ────────────────────────────────────────────────────────────

interface ProfileCardProps {
  initialName: string;
  email: string;
  role: string;
  tenantId: string | null;
}

function ProfileCard({ initialName, email, role, tenantId }: ProfileCardProps) {
  const updateMe = useUpdateMe();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: initialName },
  });

  // Sincroniza se o usuário for atualizado por outra ação (ex.: invalidateQueries).
  useEffect(() => {
    reset({ name: initialName });
  }, [initialName, reset]);

  async function onSubmit(data: ProfileForm) {
    updateMe.reset();
    try {
      await updateMe.mutateAsync({ name: data.name });
    } catch {
      // erro já exposto na UI via updateMe.error; sem catch a rejeição
      // escapa do handleSubmit do RHF como unhandled rejection.
      return;
    }
    // Reseta isDirty após sucesso para refletir o estado salvo.
    reset({ name: data.name });
  }

  return (
    <Card>
      <h2 className="mb-4 text-base font-semibold text-[var(--color-foreground)]">
        Perfil
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Nome */}
        <div className="space-y-1.5">
          <Label htmlFor="profile-name">Nome</Label>
          <Input
            id="profile-name"
            placeholder="Seu nome"
            aria-invalid={!!errors.name}
            {...register("name")}
          />
          {errors.name ? (
            <p className="text-xs text-[var(--color-destructive)]" role="alert">
              {errors.name.message}
            </p>
          ) : null}
        </div>

        {/* Email — read-only */}
        <div className="space-y-1.5">
          <Label htmlFor="profile-email">Email</Label>
          <Input
            id="profile-email"
            value={email}
            readOnly
            disabled
            aria-describedby="profile-email-hint"
          />
          <p
            id="profile-email-hint"
            className="text-xs text-[var(--color-muted-foreground)]"
          >
            Não é possível alterar o email por aqui.
          </p>
        </div>

        {/* Role — read-only */}
        <div className="space-y-1.5">
          <Label htmlFor="profile-role">Nível de acesso</Label>
          <Input
            id="profile-role"
            value={ROLE_LABEL[role] ?? role}
            readOnly
            disabled
          />
        </div>

        {/* Tenant ID — read-only, só aparece quando preenchido */}
        {tenantId ? (
          <div className="space-y-1.5">
            <Label htmlFor="profile-tenant">Cliente (tenant)</Label>
            <Input
              id="profile-tenant"
              value={tenantId}
              readOnly
              disabled
              className="font-mono text-xs"
            />
          </div>
        ) : null}

        {/* Feedback de sucesso/erro */}
        {updateMe.isSuccess ? (
          <div
            role="status"
            className="rounded-md border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 px-4 py-3 text-sm text-[var(--color-success)]"
          >
            Dados salvos com sucesso.
          </div>
        ) : null}
        {updateMe.isError ? (
          <div
            role="alert"
            className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]"
          >
            {updateMe.error instanceof Error
              ? updateMe.error.message
              : "Erro ao salvar. Tente novamente."}
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={!isDirty || isSubmitting || updateMe.isPending}
          aria-busy={isSubmitting || updateMe.isPending}
        >
          {updateMe.isPending ? "Salvando…" : "Salvar"}
        </Button>
      </form>
    </Card>
  );
}

// ── PasswordCard ───────────────────────────────────────────────────────────

function PasswordCard() {
  const changePassword = useChangePassword();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    mode: "onSubmit",
  });

  async function onSubmit(data: PasswordForm) {
    changePassword.reset();
    try {
      await changePassword.mutateAsync({
        current_password: data.current_password,
        new_password: data.new_password,
      });
    } catch {
      // erro já exposto na UI via changePassword.error; sem catch a rejeição
      // escapa do handleSubmit do RHF como unhandled rejection.
      return;
    }
    // Limpa o formulário após troca bem-sucedida.
    reset();
  }

  return (
    <Card>
      <h2 className="mb-4 text-base font-semibold text-[var(--color-foreground)]">
        Trocar senha
      </h2>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Senha atual */}
        <div className="space-y-1.5">
          <Label htmlFor="pwd-current">Senha atual</Label>
          <Input
            id="pwd-current"
            type="password"
            autoComplete="current-password"
            aria-invalid={!!errors.current_password}
            {...register("current_password")}
          />
          {errors.current_password ? (
            <p className="text-xs text-[var(--color-destructive)]" role="alert">
              {errors.current_password.message}
            </p>
          ) : null}
        </div>

        {/* Nova senha */}
        <div className="space-y-1.5">
          <Label htmlFor="pwd-new">Nova senha</Label>
          <Input
            id="pwd-new"
            type="password"
            autoComplete="new-password"
            aria-describedby="pwd-new-hint"
            aria-invalid={!!errors.new_password}
            {...register("new_password")}
          />
          <p
            id="pwd-new-hint"
            className="text-xs text-[var(--color-muted-foreground)]"
          >
            Mínimo de 12 caracteres.
          </p>
          {errors.new_password ? (
            <p className="text-xs text-[var(--color-destructive)]" role="alert">
              {errors.new_password.message}
            </p>
          ) : null}
        </div>

        {/* Confirmar nova senha */}
        <div className="space-y-1.5">
          <Label htmlFor="pwd-confirm">Confirmar nova senha</Label>
          <Input
            id="pwd-confirm"
            type="password"
            autoComplete="new-password"
            aria-invalid={!!errors.confirm_password}
            {...register("confirm_password")}
          />
          {errors.confirm_password ? (
            <p className="text-xs text-[var(--color-destructive)]" role="alert">
              {errors.confirm_password.message}
            </p>
          ) : null}
        </div>

        {/* Feedback de sucesso/erro */}
        {changePassword.isSuccess ? (
          <div
            role="status"
            className="rounded-md border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 px-4 py-3 text-sm text-[var(--color-success)]"
          >
            Senha alterada com sucesso. Outros dispositivos foram desconectados.
          </div>
        ) : null}
        {changePassword.isError ? (
          <div
            role="alert"
            className="rounded-md border border-[var(--color-destructive)]/40 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]"
          >
            {changePassword.error instanceof Error
              ? changePassword.error.message
              : "Erro ao trocar senha. Tente novamente."}
          </div>
        ) : null}

        <Button
          type="submit"
          disabled={isSubmitting || changePassword.isPending}
          aria-busy={isSubmitting || changePassword.isPending}
        >
          {changePassword.isPending ? "Salvando…" : "Trocar senha"}
        </Button>
      </form>
    </Card>
  );
}

// ── MeusDadosPage ──────────────────────────────────────────────────────────

export function MeusDadosPage() {
  const { user, isLoading } = useAuth();

  // Estado de carregamento: skeleton cards.
  if (isLoading) {
    return (
      <RequireAuth>
        <AppLayout breadcrumb="CONTA / MEUS DADOS" title="Meus dados">
          <div className="mx-auto max-w-xl space-y-6">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-72 w-full" />
          </div>
        </AppLayout>
      </RequireAuth>
    );
  }

  // Fallback: não deve chegar aqui em produção (RequireAuth redireciona),
  // mas satisfaz o typechecker e serve de guard explícito.
  if (!user) {
    return null;
  }

  return (
    <RequireAuth>
      <AppLayout breadcrumb="CONTA / MEUS DADOS" title="Meus dados">
        <div className="mx-auto max-w-xl space-y-6">
          <ProfileCard
            initialName={user.name ?? ""}
            email={user.email}
            role={user.role}
            tenantId={user.tenant_id}
          />
          <PasswordCard />
        </div>
      </AppLayout>
    </RequireAuth>
  );
}
